import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, AlertTriangle, Info, Loader2, Play, FileJson, RefreshCw } from 'lucide-react';
import { use, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'sonner';
import { AppDataContext } from '@/lib/app-data-hydration';
import { useAdminVerification, type VerificationIssue, type VerificationResult } from './AdminVerificationContext';

export default function AdminContextDataVerification() {
    const { data: appData } = use(AppDataContext);
    const {
        contextResult: result,
        setContextResult: setResult,
        isVerifyingContext: isVerifying,
        setIsVerifyingContext: setIsVerifying
    } = useAdminVerification();

    const [expandedIssues, setExpandedIssues] = useState<Set<number>>(new Set());
    const [regeneratingDocs, setRegeneratingDocs] = useState<Set<string>>(new Set());

    const toggleIssueExpansion = (issueIndex: number) => {
        const newExpanded = new Set(expandedIssues);
        if (newExpanded.has(issueIndex)) {
            newExpanded.delete(issueIndex);
        } else {
            newExpanded.add(issueIndex);
        }
        setExpandedIssues(newExpanded);
    };

    const handleVerify = async () => {
        if (!confirm('Run context data verification? This will check all context collections for required fields and valid enum values.')) {
            return;
        }

        setIsVerifying(true);
        setResult(null);

        const functions = getFunctions();
        const verifyFunction = httpsCallable(functions, 'verifyContextDataFunction', {
            timeout: 300000 // 5 minutes
        });

        try {
            console.log('Starting context data verification...');
            const response = await verifyFunction({}) as any;
            console.log('Verification complete:', response);

            setResult(response.data as VerificationResult);
        } catch (error: any) {
            console.error('Verification failed:', error);
            setResult({
                success: false,
                timestamp: new Date().toISOString(),
                issues: [{
                    type: 'error',
                    category: 'system-error',
                    message: error.message || 'Unknown error occurred'
                }],
                stats: {}
            });
        } finally {
            setIsVerifying(false);
        }
    };


    /**
     * Parse document path from error message (e.g., "archaeologyByPassage/abc123" -> {collection, docId})
     */
    const parseDocumentPath = (message: string): { collection: string; docId: string; book?: string } | null => {
        // Error messages start with "collection/docId"
        const match = message.match(/^([^/]+)\/([^.:]+)/);
        if (!match) return null;

        const collection = match[1];
        const docId = match[2];

        // For by-passage collections, docId is the passage ID
        // For by-book collections, docId is the book name
        const isByBook = collection.endsWith('ByBook');

        return {
            collection,
            docId,
            book: isByBook ? docId : undefined
        };
    };

    /**
     * Regenerate a specific document based on its collection and ID
     */
    const handleRegenerate = async (issue: VerificationIssue) => {
        const parsed = parseDocumentPath(issue.message);
        if (!parsed) {
            toast.error('Cannot regenerate', {
                description: 'Unable to parse document path from error message'
            });
            return;
        }

        const { collection, docId, book } = parsed;
        const docPath = `${collection}/${docId}`;

        if (!confirm(`Regenerate ${docPath}?`)) {
            return;
        }

        setRegeneratingDocs(prev => new Set(prev).add(docPath));

        const functions = getFunctions();

        try {
            let functionName: string;
            let params: any = {};

            // Map collection to appropriate generation function
            switch (collection) {
                case 'archaeologyByPassage':
                    // Get book name from passage data
                    const archaeologyPassage = appData?.passages?.find(p => p.id === docId);
                    if (!archaeologyPassage) {
                        toast.error('Cannot regenerate', {
                            description: `Passage ${docId} not found in app data`
                        });
                        setRegeneratingDocs(prev => {
                            const next = new Set(prev);
                            next.delete(docPath);
                            return next;
                        });
                        return;
                    }
                    functionName = 'generateArchaeologyByPassageForBook';
                    params = { book: archaeologyPassage.book, passageIds: [docId] };
                    break;

                case 'archaeologyByBook':
                    functionName = 'generateArchaeologyByBook';
                    params = { book: docId };
                    break;

                case 'cultureByPassage':
                    // Get book name from passage data
                    const culturePassage = appData?.passages?.find(p => p.id === docId);
                    if (!culturePassage) {
                        toast.error('Cannot regenerate', {
                            description: `Passage ${docId} not found in app data`
                        });
                        setRegeneratingDocs(prev => {
                            const next = new Set(prev);
                            next.delete(docPath);
                            return next;
                        });
                        return;
                    }
                    functionName = 'generateCultureByPassageForBook';
                    params = { book: culturePassage.book, passageIds: [docId] };
                    break;

                case 'cultureByBook':
                    functionName = 'generateCultureByBook';
                    params = { book: docId };
                    break;

                case 'placesByPassage':
                    // Get book name from passage data
                    const placesPassage = appData?.passages?.find(p => p.id === docId);
                    if (!placesPassage) {
                        toast.error('Cannot regenerate', {
                            description: `Passage ${docId} not found in app data`
                        });
                        setRegeneratingDocs(prev => {
                            const next = new Set(prev);
                            next.delete(docPath);
                            return next;
                        });
                        return;
                    }
                    functionName = 'generatePlacesByPassageForBook';
                    params = { book: placesPassage.book, passageIds: [docId] };
                    break;

                case 'historicalEventsByPassage':
                    // Get book name from passage data
                    const historicalEventsPassage = appData?.passages?.find(p => p.id === docId);
                    if (!historicalEventsPassage) {
                        toast.error('Cannot regenerate', {
                            description: `Passage ${docId} not found in app data`
                        });
                        setRegeneratingDocs(prev => {
                            const next = new Set(prev);
                            next.delete(docPath);
                            return next;
                        });
                        return;
                    }
                    functionName = 'generateHistoricalEventsByPassageForBook';
                    params = { book: historicalEventsPassage.book, passageIds: [docId] };
                    break;

                case 'historicalEventsByBook':
                    functionName = 'generateHistoricalEventsByBook';
                    params = { book: docId };
                    break;

                case 'studyGuideByPassage':
                    // Get book name from passage data
                    const studyGuidePassage = appData?.passages?.find(p => p.id === docId);
                    if (!studyGuidePassage) {
                        toast.error('Cannot regenerate', {
                            description: `Passage ${docId} not found in app data`
                        });
                        setRegeneratingDocs(prev => {
                            const next = new Set(prev);
                            next.delete(docPath);
                            return next;
                        });
                        return;
                    }
                    functionName = 'generateStudyGuideByPassage';
                    params = { passageId: docId };
                    break;

                default:
                    toast.error('Not supported', {
                        description: `Regeneration not yet supported for ${collection}`
                    });
                    setRegeneratingDocs(prev => {
                        const next = new Set(prev);
                        next.delete(docPath);
                        return next;
                    });
                    return;
            }

            const generateFunction = httpsCallable(functions, functionName, {
                timeout: 180000 // 3 minutes
            });

            console.log(`Regenerating ${docPath} with ${functionName}...`, params);
            await generateFunction(params);

            toast.success('Regeneration complete', {
                description: `Successfully regenerated ${docPath}`
            });

            // Optionally re-run verification to update the UI
            // await handleVerify();

        } catch (error: any) {
            console.error('Regeneration failed:', error);
            toast.error('Regeneration failed', {
                description: error.message || 'Unknown error occurred'
            });
        } finally {
            setRegeneratingDocs(prev => {
                const next = new Set(prev);
                next.delete(docPath);
                return next;
            });
        }
    };

    const getIssueIcon = (type: string) => {
        switch (type) {
            case 'error':
                return <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />;
            case 'warning':
                return <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0" />;
            case 'info':
                return <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />;
            default:
                return null;
        }
    };

    const groupIssuesByCategory = (issues: VerificationIssue[]) => {
        const grouped = new Map<string, VerificationIssue[]>();
        issues.forEach(issue => {
            const existing = grouped.get(issue.category) || [];
            existing.push(issue);
            grouped.set(issue.category, existing);
        });
        return grouped;
    };

    const groupIssuesByCollection = (issues: VerificationIssue[]) => {
        const grouped = new Map<string, VerificationIssue[]>();
        issues.forEach(issue => {
            // Extract collection from message (format: "collection/docId: ...")
            const match = issue.message.match(/^([^/]+)\//);
            const collection = match ? match[1] : 'unknown';
            const existing = grouped.get(collection) || [];
            existing.push(issue);
            grouped.set(collection, existing);
        });
        return grouped;
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Context Data Verification
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="text-sm text-muted-foreground">
                    Verify the integrity of context data collections:
                    <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>All required fields are present</li>
                        <li>Enum values are valid (confidence, importance, precision, etc.)</li>
                        <li>Date proposals have required fields</li>
                        <li>Categories are valid and have subcategories where required</li>
                        <li>Coordinates and numeric fields are valid</li>
                    </ul>
                </div>

                <div className="text-sm text-muted-foreground">
                    <strong>Collections checked:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-0.5">
                        <li>archaeologyByPassage, archaeologyByBook</li>
                        <li>cultureByPassage, cultureByBook</li>
                        <li>placesByPassage, places</li>
                        <li>historicalEvents, historicalEventsByPassage, historicalEventsByBook</li>
                        <li>people, peopleByPassage</li>
                        <li>literaryFeaturesByBook, literaryFeaturesByPassage</li>
                        <li>studyGuideByPassage</li>
                    </ul>
                </div>

                <Button
                    onClick={handleVerify}
                    disabled={isVerifying}
                    className="w-full"
                >
                    {isVerifying ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Verifying...
                        </>
                    ) : (
                        <>
                            <Play className="mr-2 h-4 w-4" />
                            Run Verification
                        </>
                    )}
                </Button>

                {result && (
                    <div className="space-y-4 mt-6">
                        <div className="p-4 bg-muted rounded-lg">
                            <div className="text-sm font-medium mb-2">Verification Summary</div>
                            <div className="text-xs text-muted-foreground">
                                Completed at: {new Date(result.timestamp).toLocaleString()}
                            </div>
                            <div className="mt-3 space-y-2">
                                {Object.entries(result.stats as Record<string, any>).map(([collection, stats]) => (
                                    <div key={collection} className="flex items-center justify-between text-sm">
                                        <span className="font-mono text-xs">{collection}</span>
                                        <div className="flex items-center gap-3">
                                            <span className="text-muted-foreground">
                                                {stats.total} total
                                            </span>
                                            {stats.valid > 0 && (
                                                <span className="text-green-600 flex items-center gap-1">
                                                    <CheckCircle className="h-3 w-3" />
                                                    {stats.valid}
                                                </span>
                                            )}
                                            {stats.errors > 0 && (
                                                <span className="text-destructive flex items-center gap-1">
                                                    <XCircle className="h-3 w-3" />
                                                    {stats.errors}
                                                </span>
                                            )}
                                            {stats.warnings > 0 && (
                                                <span className="text-yellow-600 flex items-center gap-1">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    {stats.warnings}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {result.issues.length === 0 ? (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800">
                                <CheckCircle className="h-5 w-5" />
                                <span className="font-medium">All data is valid!</span>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="text-sm font-medium">
                                    Found {result.issues.length} issue{result.issues.length !== 1 ? 's' : ''}:
                                    <span className="ml-2 text-destructive">
                                        {result.issues.filter(i => i.type === 'error').length} errors
                                    </span>
                                    <span className="ml-2 text-yellow-600">
                                        {result.issues.filter(i => i.type === 'warning').length} warnings
                                    </span>
                                </div>

                                {/* Group by collection */}
                                {Array.from(groupIssuesByCollection(result.issues)).map(([collection, collectionIssues]) => (
                                    <div key={collection} className="border rounded-lg p-4">
                                        <div className="font-medium text-sm mb-3 font-mono">
                                            {collection} ({collectionIssues.length} issue{collectionIssues.length !== 1 ? 's' : ''})
                                        </div>

                                        {/* Group by category within collection */}
                                        {Array.from(groupIssuesByCategory(collectionIssues)).map(([category, categoryIssues]) => (
                                            <div key={category} className="mb-4 last:mb-0">
                                                <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                                                    {category} ({categoryIssues.length})
                                                </div>
                                                <div className="space-y-1">
                                                    {categoryIssues.slice(0, 10).map((issue, idx) => {
                                                        const globalIdx = result.issues.indexOf(issue);
                                                        const isExpanded = expandedIssues.has(globalIdx);

                                                        const parsed = parseDocumentPath(issue.message);
                                                        const docPath = parsed ? `${parsed.collection}/${parsed.docId}` : null;
                                                        const isRegenerating = docPath ? regeneratingDocs.has(docPath) : false;

                                                        return (
                                                            <div key={idx} className="border rounded p-2 bg-muted/50">
                                                                <div className="flex items-start gap-2 text-xs">
                                                                    {getIssueIcon(issue.type)}
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="break-words">{issue.message}</div>
                                                                        {issue.details && (
                                                                            <div className="text-muted-foreground mt-1 font-mono text-[10px]">
                                                                                {JSON.stringify(issue.details)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex gap-1 flex-shrink-0">
                                                                        {parsed && (
                                                                            <Button
                                                                                size="sm"
                                                                                variant="ghost"
                                                                                onClick={() => handleRegenerate(issue)}
                                                                                disabled={isRegenerating}
                                                                                className="h-6 px-2"
                                                                                title="Regenerate this document"
                                                                            >
                                                                                {isRegenerating ? (
                                                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                                                ) : (
                                                                                    <RefreshCw className="h-3 w-3" />
                                                                                )}
                                                                            </Button>
                                                                        )}
                                                                        {issue.documentData && (
                                                                            <Button
                                                                                size="sm"
                                                                                variant="ghost"
                                                                                onClick={() => toggleIssueExpansion(globalIdx)}
                                                                                className="h-6 px-2"
                                                                                title="View full document JSON"
                                                                            >
                                                                                <FileJson className="h-3 w-3" />
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {isExpanded && issue.documentData && (
                                                                    <div className="mt-2 p-2 bg-background border rounded">
                                                                        <div className="flex items-center justify-between mb-1">
                                                                            <span className="text-xs font-medium">Full Document Data:</span>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="ghost"
                                                                                onClick={() => {
                                                                                    navigator.clipboard.writeText(JSON.stringify(issue.documentData, null, 2));
                                                                                }}
                                                                                className="h-6 px-2 text-xs"
                                                                            >
                                                                                Copy JSON
                                                                            </Button>
                                                                        </div>
                                                                        <pre className="text-[10px] font-mono overflow-x-auto max-h-96 overflow-y-auto">
                                                                            {JSON.stringify(issue.documentData, null, 2)}
                                                                        </pre>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                    {categoryIssues.length > 10 && (
                                                        <div className="text-xs text-muted-foreground italic pl-6">
                                                            ... and {categoryIssues.length - 10} more
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

